import { Document, Image, Page, Text, View } from '@react-pdf/renderer';
import type { DateFormat } from '@witmetrics/utils';
import type { TraceabilityData } from '../types';
import { DECLARATION } from '../constants';
import { displayDate, displayValue, generateQrCode, styles } from './utils';

export type AttestPdfProps = {
  logoSrc: string;
  dateFormat?: DateFormat;
  data: TraceabilityData;
};

export default function AttestPdf({
  logoSrc,
  dateFormat,
  data,
}: AttestPdfProps) {
  const isLongDescription = data.orderDescription.length > 200;

  return (
    <Document>
      <Page style={styles.page.wrapper} size="A4">
        <View style={styles.header.wrapper}>
          <Image style={styles.header.logo} src={logoSrc} />
          <View>
            <Text style={styles.header.title}>Device Traceability Record</Text>
            <Text style={styles.header.subtitle}>
              In accordance with Annex XIII of Regulation (EU) 2017/745 (MDR)
            </Text>
          </View>
        </View>
        <View style={styles.section.wrapper}>
          <Text style={styles.section.title}>Resin Information</Text>
          <View style={styles.field.wrapper}>
            <Text style={styles.field.label}>Resin Trade Name</Text>
            <Text style={styles.field.value}>
              {displayValue(data.resinName)}
            </Text>
          </View>
          <View style={styles.field.wrapper}>
            <Text style={styles.field.label}>Resin Lot / Batch Number</Text>
            <Text style={styles.field.value}>
              {displayValue(data.lotNumber)}
            </Text>
          </View>
          <View style={styles.field.wrapper}>
            <Text style={styles.field.label}>Resin Expiry Date</Text>
            <Text style={styles.field.value}>
              {displayDate(data.expiryDate, dateFormat)}
            </Text>
          </View>
        </View>
        <View style={styles.section.wrapper}>
          <Text style={styles.section.title}>Patient & Device Information</Text>
          <View style={styles.field.wrapper}>
            <Text style={styles.field.label}>Patient Name / Code</Text>
            <Text style={styles.field.value}>
              {displayValue(data.patientCode)}
            </Text>
          </View>
          <View style={styles.field.wrapper}>
            <Text style={styles.field.label}>Prescription / Order Number</Text>
            <Text style={styles.field.value}>
              {displayValue(data.orderNumber)}
            </Text>
          </View>
          <View
            style={{
              ...styles.field.wrapper,
              ...(isLongDescription ? styles.field.longWrapper : {}),
            }}>
            <Text style={styles.field.label}>
              Prescription / Order Description
            </Text>
            <Text
              style={{
                ...styles.field.value,
                ...(isLongDescription ? styles.field.longValue : {}),
              }}>
              {displayValue(data.orderDescription)}
            </Text>
          </View>
          <View style={styles.field.wrapper}>
            <Text style={styles.field.label}>
              Date of Manufacture of Final Device
            </Text>
            <Text style={styles.field.value}>
              {displayDate(data.manufactureDate, dateFormat)}
            </Text>
          </View>
          <View style={styles.field.wrapper}>
            <Text style={styles.field.label}>Dental Lab / Clinic Name</Text>
            <Text style={styles.field.value}>
              {displayValue(data.clinicName)}
            </Text>
          </View>
          <View style={styles.field.wrapper}>
            <Text style={styles.field.label}>Address of Lab / Clinic</Text>
            <Text style={styles.field.value}>{displayValue(data.address)}</Text>
          </View>
          <View style={styles.field.wrapper}>
            <Text style={styles.field.label}>
              Name of Responsible Dental Professional
            </Text>
            <Text style={styles.field.value}>
              {displayValue(data.ownerName)}
            </Text>
          </View>
        </View>
        <View style={styles.section.wrapper}>
          <Text style={styles.section.title}>Mandatory Declaration</Text>
          <View
            style={{ ...styles.field.wrapper, ...styles.field.longWrapper }}>
            <Text style={{ ...styles.field.value, ...styles.field.longValue }}>
              {DECLARATION}
            </Text>
          </View>
        </View>
        <View style={styles.footer.wrapper}>
          <Image style={styles.footer.qrCode} src={generateQrCode(data)} />
        </View>
      </Page>
    </Document>
  );
}
